import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import {
  AHL_PALETTE_IDS,
  CASUALTY_PALETTE_IDS,
  layerIds,
  PROPERTY_PALETTE_IDS,
  STANDARD_PALETTE_IDS,
} from '../../analysis/model/layer-palette.model'
import { Store, select } from '@ngrx/store'
import { AppState } from 'src/app/core/store'
import { take, takeUntil } from 'rxjs/operators'
import { MessageDialogService } from '@shared/message-dialog.service'
import { MatDialog } from '@angular/material/dialog'

@Injectable({
  providedIn: 'root',
})
export class LayerService implements OnDestroy {
  private destroy$ = new Subject()
  layerCopyObs$: BehaviorSubject<any> = new BehaviorSubject({} as any)

  constructor(
    private store: Store<AppState>,
    private messageService: MessageDialogService,
    public dialog: MatDialog,
  ) {}

  setLayerCopyObs(layer: any): void {
    this.layerCopyObs$.next(layer)
  }
  getLayerCopyObs(): Observable<any> {
    return this.layerCopyObs$.asObservable()
  }

  buildCopiedLayer(
    layer: any,
    lossSetLayers: any[],
    currency: string,
    structureId: string | null,
  ): any {
    const logicalIDTemp = new Date().getTime() + '' + Math.random() * 100000
    const { meta_data, physicalLayer, viewMetrics, sharedLayerID, layerRefs } = layer
    const { sage_layer_type, sage_layer_subtype } = meta_data
    const layerName = physicalLayer.description + ' Copy'

    const isFHCFFinal = sage_layer_type === layerIds.catFhcf
    const attachmentValue =
      physicalLayer.participation >= -0.5
        ? physicalLayer.attachment.value
        : physicalLayer.attachment.value + physicalLayer.limit.value
    const copyLayer: any = {
      id: logicalIDTemp,
      meta_data: {
        client: meta_data.client,
        isFHCFFinal,
        layerName,
        perspective: meta_data.perspective || '',
        rol_type: meta_data.rol_type,
        sage_layer_type,
        sage_layer_subtype,
        structureID: structureId ?? meta_data.structureID,
        year: meta_data.year || '',
        copiedLayerId: layer.id,
      },
      layerRefs,
      lossSetFilter: '',
      lossSetLayers: this.getLossLayers(lossSetLayers, sage_layer_type, sage_layer_subtype),
      physicalLayer: {
        aggregateAttachment: {
          value: physicalLayer.aggregateAttachment.value,
          currency,
        },
        aggregateLimit: {
          value: physicalLayer.aggregateLimit.value,
          currency,
        },
        attachment: {
          value: attachmentValue,
          currency,
        },
        description: layerName,
        fees: [],
        franchise: {
          value: physicalLayer.franchise.value,
          currency,
        },
        id: logicalIDTemp.replace('.', ''),
        logicalLayerID: logicalIDTemp,
        limit: {
          value: physicalLayer.limit.value,
          currency,
        },
        participation: physicalLayer.participation,
        premium: {
          value: physicalLayer.premium.value,
          currency,
        },
        meta_data: {
          client: meta_data.client || '',
          perspective: meta_data.perspective || '',
          rol_type: meta_data.rol_type,
          rol: 0.0,
          sage_layer_type,
          year: meta_data.year || '',
          sage_layer_subtype,
          isFHCFFinal,
          payout: meta_data.payout,
          pricingCurves: physicalLayer.meta_data.pricingCurves,
          pricingcurve_is_default: physicalLayer.meta_data.pricingcurve_is_default,
          reinsurers: physicalLayer.meta_data.reinsurers,
        },
        reinstatements: physicalLayer.reinstatements,
        type: physicalLayer.type,
        xcoord: -1,
        ycoord: -1,
        inception_date: physicalLayer.inception_date,
        expiry_date: physicalLayer.expiry_date,
        payout: physicalLayer.payout,
        trigger: physicalLayer.trigger,
        nth: physicalLayer.nth,
      },
      sharedLayerID,
      viewMetrics: {
        error: null,
        loading: false,
        metrics: viewMetrics.metrics,
        rss: null,
      },
    }
    if (physicalLayer.riskAttachment && physicalLayer.riskAttachment.value) {
      copyLayer.physicalLayer.riskAttachment = {
        value: physicalLayer.riskAttachment.value,
        currency,
      }
    }
    if (physicalLayer.riskLimit && physicalLayer.riskLimit.value) {
      copyLayer.physicalLayer.riskLimit = {
        value: physicalLayer.riskLimit.value,
        currency,
      }
    }
    return copyLayer
  }

  getLossLayers(
    lossSetLayers: any[],
    type: string | undefined,
    subtype: string | undefined,
  ): any[] {
    const lossLayers: any[] = []
    if (type === layerIds.noncatIndxl) {
      lossSetLayers.forEach((a) => {
        if (a.meta_data.loss_type === 'large') {
          lossLayers.push({
            id: a.id,
            meta_data: a.meta_data,
          })
        }
      })
    } else if (type === layerIds.noncatXl) {
      const attrOnlySets = lossSetLayers.filter((x) => x.meta_data.loss_type === 'attr')
      const layerLossSets = attrOnlySets.length > 0 ? attrOnlySets : lossSetLayers
      layerLossSets.forEach(({ id, meta_data }) => lossLayers.push({ id, meta_data }))
    } else if (!this.isAggFeeder(type, subtype)) {
      lossSetLayers.forEach(({ id, meta_data }) => lossLayers.push({ id, meta_data }))
    }
    return lossLayers
  }

  isAggFeeder(id: string | undefined, subtype: string | undefined): boolean {
    return (
      subtype !== 'feeder' &&
      (id === layerIds.catAg ||
        id === layerIds.noncatAg ||
        id === layerIds.ahlAg ||
        id === layerIds.ilwAg)
    )
  }

  isPaletteLayerFHCF = (type: string): boolean => type === layerIds.catFhcf

  isPaletteLayerRisk = (type: string): boolean => type === layerIds.noncatRisk

  isPaletteLayerTD = (type: string): boolean => type === layerIds.catTd

  isPaletteLayerAggFeeder(e: any): boolean {
    return (
      e.item.subtype === 'feeder' &&
      (e.item.id === layerIds.catAg ||
        e.item.id === layerIds.noncatAg ||
        e.item.id === layerIds.ahlAg ||
        e.item.id === layerIds.ilwAg)
    )
  }

  getLayerIds(layerPaletteViewId: string): string[] {
    switch (layerPaletteViewId) {
      case '1':
        return STANDARD_PALETTE_IDS
      case '2':
        return AHL_PALETTE_IDS
      case '3':
        return PROPERTY_PALETTE_IDS
      case '4':
        return CASUALTY_PALETTE_IDS
      default:
        return STANDARD_PALETTE_IDS
    }
  }

  deleteLayerCheck(layerRef: string): void {}

  getQuoteReinsurers(layer: any) {}

  deleteLayer(id: string): void {}

  ngOnDestroy(): void {
    this.layerCopyObs$.unsubscribe()
    this.destroy$.next(true)
    this.destroy$.complete()
  }
}
